import React, { useState } from "react";
import { X, Zap, ShieldCheck, Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Public "Try SIX Live" modal.
 *
 * Captures email only (per user pref "1c").  Calls POST /api/demo/start which
 * provisions a time-boxed test_account=true client user, sets auth cookies,
 * and returns an access_token.  Then redirects to /client?demo=1 where the
 * DemoBanner will guide the user to trigger a live SIMULATOR delivery.
 */
export default function DemoModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const { refreshMe } = useAuth();

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/demo/start", { email: email.trim().toLowerCase() });
      if (data?.access_token) {
        localStorage.setItem("dt6_access_token", data.access_token);
      }
      await refreshMe();
      // Fresh user should land in the client portal with the demo overlay live.
      nav("/client?demo=1", { replace: true });
      onClose?.();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center px-4"
      style={{ background: "rgba(6,10,20,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="demo-modal-backdrop"
    >
      <div
        className="dt6-card w-full max-w-lg p-8 relative dt6-fade-in"
        style={{ background: "var(--dt6-surface)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[color:var(--dt6-muted)] hover:text-[color:var(--dt6-fg)]"
          data-testid="demo-modal-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="dt6-chip dt6-chip-primary mb-4">/ LIVE 60-SECOND DEMO</div>
        <h2 className="font-heading text-3xl font-bold mb-3">
          Watch SIX auto-deliver in real time.
        </h2>
        <p className="text-sm text-[color:var(--dt6-muted)] mb-6">
          Drop your email and we'll spin up a sandboxed portal in seconds. Click one button
          and see the full DT6 auto-delivery pipeline fire —
          adapter → deterministic QA → deliverable in your inbox — using our SIMULATOR (not real voter data).
        </p>

        <div className="grid grid-cols-1 gap-2 mb-6">
          {[
            { icon: <Rocket size={14} />, label: "One-click sample D1 order (Pennsylvania universe pull)" },
            { icon: <Zap size={14} />, label: "Full QA checklist + auto-delivery visible in your portal" },
            { icon: <ShieldCheck size={14} />, label: "Clearly labeled SIMULATOR output — never real voter data" },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-3 text-xs text-[color:var(--dt6-muted)]">
              <span className="text-[color:var(--dt6-primary)]">{f.icon}</span>
              <span>{f.label}</span>
            </div>
          ))}
        </div>

        <form onSubmit={submit} data-testid="demo-modal-form">
          <label className="dt6-label" htmlFor="demo-modal-email">Your work email</label>
          <input
            id="demo-modal-email"
            data-testid="demo-modal-email"
            type="email"
            className="dt6-input"
            placeholder="operator@campaign.example"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          {err && (
            <div
              className="mt-3 text-sm text-[color:var(--dt6-danger)]"
              data-testid="demo-modal-error"
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="dt6-btn dt6-btn-primary mt-5 w-full justify-center"
            data-testid="demo-modal-submit"
          >
            {busy ? "SPINNING UP DEMO PORTAL..." : "Start my 60-second demo"}
          </button>
        </form>

        <p className="text-[0.7rem] tracking-wide text-[color:var(--dt6-muted)] mt-4 font-mono uppercase">
          / Demo access valid for 7 days · limited to 3 signups per network per day
        </p>
      </div>
    </div>
  );
}
